import React, { useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import FloatingAIAssistant from '../AI/FloatingAIAssistant';
import { aiApi } from '../../services/api';

function fmtDateBR(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function pct(n, d) {
  return d > 0 ? (n / d) * 100 : null;
}

function fmtPct(v) {
  return v === null || v === undefined ? '—' : `${v.toFixed(1)}%`;
}

// Derives the funnel ratios for a single unit's summary — used both per-row
// and for the group-wide aggregate (called with summed totals).
function withRates(s) {
  return {
    ...s,
    leadToApptRate: pct(s.appointments, s.leads),
    apptToAttendRate: pct(s.attendances, s.appointments),
    closingRate: pct(s.closings, s.leads),
  };
}

function FunnelBar({ label, value, colorClass = 'bg-primary' }) {
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-slate-500">{label}</span>
        <span className="font-bold text-slate-700">{fmtPct(value)}</span>
      </div>
      <div className="h-2 bg-surface-container rounded-full overflow-hidden">
        <div className={`h-full ${colorClass} rounded-full`} style={{ width: `${Math.min(value || 0, 100)}%` }} />
      </div>
    </div>
  );
}

function RankingCard({ title, icon, subtitle, rows, valueKey, valueLabel, formatValue, tone }) {
  if (rows.length === 0) return null;
  const best = rows.slice(0, 3);
  const worst = rows.slice(-3).reverse().filter(r => !best.includes(r));
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
      <h3 className="font-headline font-bold flex items-center gap-2 mb-1">
        <span className={`material-symbols-outlined text-[20px] ${tone}`}>{icon}</span>
        {title}
      </h3>
      <p className="text-xs text-slate-400 mb-4">{subtitle}</p>
      <div className="space-y-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600 mb-2">Melhores</p>
          <div className="space-y-1.5">
            {best.map(r => (
              <div key={r.routineId} className="flex items-center justify-between gap-2 bg-emerald-50/60 rounded-lg px-3 py-2">
                <span className="text-xs font-medium text-slate-700 truncate">{r.routineName}</span>
                <span className="text-xs font-bold text-emerald-700 whitespace-nowrap">{formatValue(r[valueKey])}</span>
              </div>
            ))}
          </div>
        </div>
        {worst.length > 0 && (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-red-500 mb-2">Piores</p>
            <div className="space-y-1.5">
              {worst.map(r => (
                <div key={r.routineId} className="flex items-center justify-between gap-2 bg-red-50/60 rounded-lg px-3 py-2">
                  <span className="text-xs font-medium text-slate-700 truncate">{r.routineName}</span>
                  <span className="text-xs font-bold text-red-600 whitespace-nowrap">{formatValue(r[valueKey])}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <p className="text-[10px] text-slate-400 mt-4">{valueLabel}</p>
    </div>
  );
}

// Multi-unit ("todas unidades X") variant of ClinicRoutineReportVisualization —
// kept as a sibling component rather than folded into it, since the shape
// (buckets, rankings, funnel, AI) is materially different and the single-unit
// flow must keep working untouched.
export default function ClinicMultiUnitReportVisualization({
  groupName, periodStart, periodEnd, unitsWithData = [], unitsNoData = [],
  onBack, onShare, readOnly = false, showFunnelAndGoals = true,
}) {
  const reportRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('desc');

  const rows = unitsWithData.map(u => ({
    routineId: u.routineId,
    routineName: u.routineName,
    adAccountName: u.adAccountName,
    ...withRates(u.summary),
  }));

  const detailColumns = [
    { label: 'Unidade', key: 'routineName' },
    { label: 'Leads', key: 'leads' },
    { label: 'Agend.', key: 'appointments' },
    { label: 'Compare.', key: 'attendances' },
    { label: 'Fechamentos', key: 'closings' },
    ...(showFunnelAndGoals ? [
      { label: 'Lead → Agend.', key: 'leadToApptRate' },
      { label: 'Agend. → Compare.', key: 'apptToAttendRate' },
      { label: 'Lead → Fechamento', key: 'closingRate' },
      { label: '', key: null },
    ] : [])
  ];

  const handleSort = (key) => {
    if (!key) return;
    if (sortKey === key) {
      setSortDir(dir => dir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sortedRows = sortKey
    ? [...rows].sort((a, b) => {
        const av = a[sortKey];
        const bv = b[sortKey];
        let cmp;
        if (typeof av === 'string' || typeof bv === 'string') {
          cmp = String(av ?? '').localeCompare(String(bv ?? ''), 'pt-BR');
        } else {
          cmp = (av ?? -Infinity) - (bv ?? -Infinity);
        }
        return sortDir === 'asc' ? cmp : -cmp;
      })
    : rows;

  const totals = rows.reduce((acc, r) => ({
    leads: acc.leads + (r.leads || 0),
    appointments: acc.appointments + (r.appointments || 0),
    attendances: acc.attendances + (r.attendances || 0),
    closings: acc.closings + (r.closings || 0),
  }), { leads: 0, appointments: 0, attendances: 0, closings: 0 });
  const groupRates = withRates(totals);

  const rankedByLeads = [...rows].sort((a, b) => (b.leads || 0) - (a.leads || 0));
  const rankedByAppointments = [...rows].sort((a, b) => (b.appointments || 0) - (a.appointments || 0));
  const rankedByAttendances = [...rows].sort((a, b) => (b.attendances || 0) - (a.attendances || 0));

  const aiPayload = {
    period: { startDate: periodStart, endDate: periodEnd },
    units: rows.map(r => ({
      name: r.routineName,
      leads: r.leads,
      appointments: r.appointments,
      attendances: r.attendances,
      closings: r.closings,
      closingRatePct: r.closingRate,
    })),
  };

  const handleExportPng = async () => {
    if (!reportRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(reportRef.current, { scale: 2, backgroundColor: '#ffffff', useCORS: true });
      const link = document.createElement('a');
      link.download = `relatorio_${(groupName || 'unidades').replace(/[^a-z0-9]/gi, '_').toLowerCase()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {!readOnly && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button onClick={onBack} className="px-6 py-2 rounded-full border border-outline-variant text-sm font-semibold hover:bg-white transition-all text-on-surface flex items-center gap-1">
            <span className="material-symbols-outlined text-sm">arrow_back</span> Voltar
          </button>
          <div className="flex items-center gap-2">
            <button onClick={handleExportPng} disabled={exporting} className="primary-pill-gradient text-white px-6 py-2 rounded-full text-sm font-bold hover:opacity-90 transition-all shadow-sm flex items-center gap-1 disabled:opacity-60">
              <span className="material-symbols-outlined text-sm">{exporting ? 'progress_activity' : 'image'}</span> {exporting ? 'Exportando...' : 'Exportar PNG'}
            </button>
            <button onClick={onShare} className="bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 px-6 py-2 rounded-full text-sm font-bold transition-all shadow-sm flex items-center gap-1">
              <span className="material-symbols-outlined text-sm">share</span> Compartilhar
            </button>
          </div>
        </div>
      )}

      <div ref={reportRef} className="bg-white p-6 lg:p-8 rounded-2xl border border-slate-100 shadow-sm space-y-8">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs font-bold text-primary uppercase tracking-widest mb-1">Relatório Multi-Unidades</p>
            <h2 className="font-headline font-extrabold text-3xl text-on-surface">{groupName || 'Relatório'}</h2>
            <p className="text-sm text-slate-500 mt-2">{rows.length} unidade{rows.length === 1 ? '' : 's'} com lançamentos · {unitsNoData.length} sem lançamentos</p>
          </div>
          {periodStart && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-bold shrink-0">
              <span className="material-symbols-outlined text-[16px]">calendar_month</span>
              {fmtDateBR(periodStart)} — {fmtDateBR(periodEnd)}
            </span>
          )}
        </div>

        {/* Group-wide KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { key: 'leads', label: 'Leads', icon: 'person_add', color: 'text-blue-500', bg: 'bg-blue-50' },
            { key: 'appointments', label: 'Agendamentos', icon: 'event', color: 'text-purple-500', bg: 'bg-purple-50' },
            { key: 'attendances', label: 'Comparecimentos', icon: 'how_to_reg', color: 'text-teal-500', bg: 'bg-teal-50' },
            { key: 'closings', label: 'Fechamentos', icon: 'task_alt', color: 'text-emerald-500', bg: 'bg-emerald-50' },
          ].map(kpi => (
            <div key={kpi.key} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm flex flex-col items-center justify-center text-center">
              <div className={`w-12 h-12 rounded-full ${kpi.bg} flex items-center justify-center mb-3`}>
                <span className={`material-symbols-outlined ${kpi.color} text-2xl`}>{kpi.icon}</span>
              </div>
              <h4 className="text-2xl font-black font-headline text-slate-800 tracking-tight leading-none mb-1">{Number(totals[kpi.key] || 0).toLocaleString('pt-BR')}</h4>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1">{kpi.label}</p>
            </div>
          ))}
        </div>

        {/* Group-wide ratio — Lead → Agendamento is the featured metric */}
        {showFunnelAndGoals && (
          <div className="space-y-4">
            <div className="primary-pill-gradient rounded-2xl px-6 py-6 text-white flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-white/70">Métrica Principal</p>
                <p className="text-sm font-bold mt-0.5">Lead → Agendamento</p>
              </div>
              <p className="text-4xl font-black leading-none">{fmtPct(groupRates.leadToApptRate)}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-800 rounded-xl px-5 py-4 text-white">
                <p className="text-[10px] font-medium uppercase tracking-wider text-slate-400">Agendamento → Comparecimento</p>
                <p className="text-2xl font-bold mt-1">{fmtPct(groupRates.apptToAttendRate)}</p>
              </div>
              <div className="bg-slate-800 rounded-xl px-5 py-4 text-white">
                <p className="text-[10px] font-medium uppercase tracking-wider text-slate-400">Lead → Fechamento</p>
                <p className="text-2xl font-bold mt-1">{fmtPct(groupRates.closingRate)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Best/worst rankings — leads, agendamentos, comparecimentos */}
        {rows.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <RankingCard
              title="Ranking por Leads"
              icon="person_add"
              subtitle="Unidades ordenadas por leads recebidos"
              rows={rankedByLeads}
              valueKey="leads"
              valueLabel="Número de leads no período"
              formatValue={(v) => `${Number(v || 0).toLocaleString('pt-BR')} leads`}
              tone="text-blue-500"
            />
            <RankingCard
              title="Ranking por Agendamentos"
              icon="event"
              subtitle="Unidades ordenadas por agendamentos"
              rows={rankedByAppointments}
              valueKey="appointments"
              valueLabel="Número de agendamentos no período"
              formatValue={(v) => `${Number(v || 0).toLocaleString('pt-BR')} agend.`}
              tone="text-purple-500"
            />
            <RankingCard
              title="Ranking por Comparecimentos"
              icon="how_to_reg"
              subtitle="Unidades ordenadas por comparecimentos"
              rows={rankedByAttendances}
              valueKey="attendances"
              valueLabel="Número de comparecimentos no período"
              formatValue={(v) => `${Number(v || 0).toLocaleString('pt-BR')} compar.`}
              tone="text-teal-500"
            />
          </div>
        )}

        {/* Sem lançamentos */}
        {unitsNoData.length > 0 && (
          <div className="bg-slate-50 rounded-2xl border border-dashed border-slate-200 p-5">
            <h3 className="font-headline font-bold text-slate-500 text-sm flex items-center gap-2 mb-3">
              <span className="material-symbols-outlined text-[18px]">block</span>
              Sem lançamentos no período ({unitsNoData.length})
            </h3>
            <div className="flex flex-wrap gap-2">
              {unitsNoData.map(u => (
                <span key={u.routineId} className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-medium text-slate-500">
                  {u.routineName}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Per-unit table with expandable funnel */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-slate-100">
            <h3 className="font-headline font-bold text-lg text-slate-800">Detalhamento por Unidade</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50/50">
                <tr>
                  {detailColumns.map(({ label, key }) => (
                    <th
                      key={label || 'expand'}
                      onClick={() => handleSort(key)}
                      className={`py-3 px-5 text-[10px] font-bold text-slate-500 uppercase tracking-widest whitespace-nowrap select-none ${key ? 'cursor-pointer hover:text-slate-700' : ''}`}
                    >
                      <span className="inline-flex items-center gap-1">
                        {label}
                        {key && (
                          <span className={`material-symbols-outlined text-[14px] transition-opacity ${sortKey === key ? 'opacity-100' : 'opacity-30'}`}>
                            {sortKey === key && sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                          </span>
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sortedRows.map(r => (
                  <React.Fragment key={r.routineId}>
                    <tr className={`hover:bg-slate-50/80 ${showFunnelAndGoals ? 'cursor-pointer' : ''}`} onClick={() => showFunnelAndGoals && setExpandedId(id => id === r.routineId ? null : r.routineId)}>
                      <td className="py-3 px-5 font-bold text-slate-700 whitespace-nowrap">{r.routineName}</td>
                      <td className="py-3 px-5 text-slate-700">{r.leads}</td>
                      <td className="py-3 px-5 text-slate-700">{r.appointments}</td>
                      <td className="py-3 px-5 text-slate-700">{r.attendances}</td>
                      <td className="py-3 px-5 text-slate-700 font-bold">{r.closings}</td>
                      {showFunnelAndGoals && (
                        <>
                          <td className="py-3 px-5 text-primary font-bold">{fmtPct(r.leadToApptRate)}</td>
                          <td className="py-3 px-5 text-slate-700">{fmtPct(r.apptToAttendRate)}</td>
                          <td className="py-3 px-5 text-emerald-700 font-bold">{fmtPct(r.closingRate)}</td>
                          <td className="py-3 px-5 text-slate-400">
                            <span className="material-symbols-outlined text-[18px]">{expandedId === r.routineId ? 'expand_less' : 'expand_more'}</span>
                          </td>
                        </>
                      )}
                    </tr>
                    {showFunnelAndGoals && expandedId === r.routineId && (
                      <tr>
                        <td colSpan={detailColumns.length} className="bg-slate-50/60 px-5 py-4">
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl">
                            <FunnelBar label="Lead → Agendamento" value={r.leadToApptRate} colorClass="bg-purple-500" />
                            <FunnelBar label="Agendamento → Comparecimento" value={r.apptToAttendRate} colorClass="bg-teal-500" />
                            <FunnelBar label="Lead → Fechamento" value={r.closingRate} colorClass="bg-emerald-500" />
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={detailColumns.length} className="py-16 text-center text-slate-400 text-sm">Nenhuma unidade com lançamentos no período selecionado.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {!readOnly && rows.length > 0 && (
        <FloatingAIAssistant campaignData={aiPayload} analyzeFn={aiApi.analyzeClinicUnits} />
      )}
    </div>
  );
}
